import React, { useState, useEffect } from 'react';
import { SlidersHorizontal, X, Save, RotateCcw, Check } from 'lucide-react';
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
    Button,
    Input,
    Label,
} from 'ui/shadcn';
import { cn } from 'lib/utils';

export interface CustomFilter {
    contractorName?: string;
    workTypes?: string[];
    staffName?: string;
    contractDateFrom?: string;
    contractDateTo?: string;
    unitsFrom?: number;
    unitsTo?: number;
}

export interface SavedFilter {
    id: string;
    name: string;
    filter: CustomFilter;
    createdAt: string;
}

const EMPTY_FILTER: CustomFilter = {};

const WORK_TYPE_OPTIONS = [
    { value: 'ELECTRIC', label: '전기' },
    { value: 'TELECOM', label: '통신' },
    { value: 'FIRE', label: '소방' },
] as const;

const STORAGE_KEY = 'ywenf.site-list.saved-filters';

export const loadSavedFilters = (): SavedFilter[] => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed;
    } catch {
        return [];
    }
};

const persist = (list: SavedFilter[]) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
        console.warn('필터 저장 실패:', e);
    }
};

interface CustomFilterPanelProps {
    filter: CustomFilter;
    onChange: (filter: CustomFilter) => void;
    savedFilters: SavedFilter[];
    onSavedFiltersChange: (filters: SavedFilter[]) => void;
}

const CustomFilterPanel: React.FC<CustomFilterPanelProps> = ({
    filter,
    onChange,
    savedFilters,
    onSavedFiltersChange,
}) => {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<CustomFilter>(filter);
    const [saveName, setSaveName] = useState('');
    const [showSaveInput, setShowSaveInput] = useState(false);

    // popover 열릴 때 현재 필터를 draft로 로드
    useEffect(() => {
        if (open) {
            setDraft(filter);
            setShowSaveInput(false);
            setSaveName('');
        }
    }, [open, filter]);

    const activeCount = countActive(filter);

    const applyDraft = () => {
        onChange(draft);
        setOpen(false);
    };

    const resetAll = () => {
        setDraft(EMPTY_FILTER);
        onChange(EMPTY_FILTER);
    };

    const handleSave = () => {
        const name = saveName.trim();
        if (!name) return;
        // draft 기준으로 저장 (적용 안 눌러도 저장 가능)
        onChange(draft);
        const next: SavedFilter[] = [
            ...savedFilters.filter((f) => f.name !== name), // 같은 이름 덮어쓰기
            {
                id: `${Date.now()}`,
                name,
                filter: draft,
                createdAt: new Date().toISOString(),
            },
        ];
        onSavedFiltersChange(next);
        persist(next);
        setSaveName('');
        setShowSaveInput(false);
        setOpen(false);
    };

    const toggleWorkType = (value: string) => {
        const cur = draft.workTypes || [];
        const next = cur.includes(value)
            ? cur.filter((v) => v !== value)
            : [...cur, value];
        setDraft({ ...draft, workTypes: next.length > 0 ? next : undefined });
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="secondary" size="sm" className="gap-1.5">
                    <SlidersHorizontal className="h-4 w-4" />
                    상세 필터
                    {activeCount > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold tabular-nums">
                            {activeCount}
                        </span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[420px] p-0">
                <div className="flex items-center justify-between px-4 h-10 border-b border-border">
                    <div className="text-sm font-semibold text-foreground">상세 필터</div>
                    <button
                        type="button"
                        className="text-neutral-500 hover:text-foreground transition-colors"
                        onClick={() => setOpen(false)}
                        aria-label="닫기"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="p-4 flex flex-col gap-3 max-h-[60vh] overflow-y-auto">
                    {/* 시공사 */}
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-xs">시공사</Label>
                        <Input
                            value={draft.contractorName || ''}
                            onChange={(e) =>
                                setDraft({ ...draft, contractorName: e.target.value || undefined })
                            }
                            placeholder="예: 대우건설"
                        />
                    </div>

                    {/* 공종 (다중 선택) */}
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-xs">공종 (다중 선택 가능)</Label>
                        <div className="flex gap-1.5 flex-wrap">
                            {WORK_TYPE_OPTIONS.map((opt) => {
                                const active = (draft.workTypes || []).includes(opt.value);
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => toggleWorkType(opt.value)}
                                        className={cn(
                                            'inline-flex items-center gap-1 h-8 px-3 rounded border text-sm transition-colors duration-fast',
                                            active
                                                ? 'bg-primary text-primary-foreground border-primary'
                                                : 'bg-background text-neutral-700 border-input hover:bg-muted'
                                        )}
                                    >
                                        {active && <Check className="h-3 w-3" />}
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* 감리원 이름 */}
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-xs">감리원 이름</Label>
                        <Input
                            value={draft.staffName || ''}
                            onChange={(e) =>
                                setDraft({ ...draft, staffName: e.target.value || undefined })
                            }
                            placeholder="예: 이수빈"
                        />
                    </div>

                    {/* 계약일 범위 */}
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-xs">계약일 범위</Label>
                        <div className="flex items-center gap-2">
                            <Input
                                type="date"
                                value={draft.contractDateFrom || ''}
                                onChange={(e) =>
                                    setDraft({
                                        ...draft,
                                        contractDateFrom: e.target.value || undefined,
                                    })
                                }
                                className="flex-1"
                            />
                            <span className="text-muted-foreground text-sm">~</span>
                            <Input
                                type="date"
                                value={draft.contractDateTo || ''}
                                onChange={(e) =>
                                    setDraft({
                                        ...draft,
                                        contractDateTo: e.target.value || undefined,
                                    })
                                }
                                className="flex-1"
                            />
                        </div>
                    </div>

                    {/* 세대수 범위 */}
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-xs">세대수 범위</Label>
                        <div className="flex items-center gap-2">
                            <Input
                                type="number"
                                min={0}
                                value={draft.unitsFrom ?? ''}
                                onChange={(e) =>
                                    setDraft({
                                        ...draft,
                                        unitsFrom:
                                            e.target.value === ''
                                                ? undefined
                                                : Number(e.target.value),
                                    })
                                }
                                placeholder="최소"
                                className="flex-1"
                            />
                            <span className="text-muted-foreground text-sm">~</span>
                            <Input
                                type="number"
                                min={0}
                                value={draft.unitsTo ?? ''}
                                onChange={(e) =>
                                    setDraft({
                                        ...draft,
                                        unitsTo:
                                            e.target.value === ''
                                                ? undefined
                                                : Number(e.target.value),
                                    })
                                }
                                placeholder="최대"
                                className="flex-1"
                            />
                        </div>
                    </div>
                </div>

                {/* 하단 액션 */}
                <div className="border-t border-border">
                    {showSaveInput ? (
                        <div className="p-3 flex items-center gap-2 bg-neutral-50">
                            <Input
                                value={saveName}
                                onChange={(e) => setSaveName(e.target.value)}
                                placeholder="필터 이름 (예: 서울 진행중 대형)"
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSave();
                                    if (e.key === 'Escape') {
                                        setShowSaveInput(false);
                                        setSaveName('');
                                    }
                                }}
                            />
                            <Button
                                size="sm"
                                onClick={handleSave}
                                disabled={!saveName.trim()}
                            >
                                저장
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                    setShowSaveInput(false);
                                    setSaveName('');
                                }}
                            >
                                취소
                            </Button>
                        </div>
                    ) : (
                        <div className="p-3 flex items-center justify-between gap-2 bg-neutral-50">
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={resetAll}
                                disabled={activeCount === 0 && countActive(draft) === 0}
                            >
                                <RotateCcw />
                                초기화
                            </Button>
                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => setShowSaveInput(true)}
                                    disabled={countActive(draft) === 0}
                                    title="현재 조건을 이름으로 저장"
                                >
                                    <Save />
                                    저장
                                </Button>
                                <Button size="sm" onClick={applyDraft}>
                                    적용
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
};

export const countActive = (f: CustomFilter): number => {
    let n = 0;
    if (f.contractorName?.trim()) n++;
    if (f.workTypes && f.workTypes.length > 0) n++;
    if (f.staffName?.trim()) n++;
    if (f.contractDateFrom || f.contractDateTo) n++;
    if (f.unitsFrom !== undefined || f.unitsTo !== undefined) n++;
    return n;
};

/** 사이트 데이터가 커스텀 필터에 매칭되는지 */
export const matchesCustomFilter = (site: any, f: CustomFilter): boolean => {
    if (f.contractorName?.trim()) {
        const q = f.contractorName.trim().toLowerCase();
        if (!(site.contractorName || '').toLowerCase().includes(q)) return false;
    }
    if (f.workTypes && f.workTypes.length > 0) {
        const siteTypes: string[] = site.workTypes || [];
        // 사이트가 요청된 공종 중 하나라도 포함해야 매칭
        const hasAny = f.workTypes.some((wt) => siteTypes.includes(wt));
        if (!hasAny) return false;
    }
    if (f.staffName?.trim()) {
        const q = f.staffName.trim().toLowerCase();
        const staffByWorkType = site.staffByWorkType || {};
        const allNames: string[] = [];
        ['ELECTRIC', 'TELECOM', 'FIRE'].forEach((wt) => {
            const wtStaff = staffByWorkType[wt] || {};
            (wtStaff.chief || []).forEach((n: string) => allNames.push(n));
            (wtStaff.assistant || []).forEach((n: string) => allNames.push(n));
        });
        if (!allNames.some((n) => n.toLowerCase().includes(q))) return false;
    }
    if (f.contractDateFrom) {
        if (!site.contractDate || site.contractDate < f.contractDateFrom) return false;
    }
    if (f.contractDateTo) {
        if (!site.contractDate || site.contractDate > f.contractDateTo) return false;
    }
    const units = parseInt(String(site.numberOfUnits || '').replace(/[^0-9]/g, ''), 10);
    if (f.unitsFrom !== undefined) {
        if (isNaN(units) || units < f.unitsFrom) return false;
    }
    if (f.unitsTo !== undefined) {
        if (isNaN(units) || units > f.unitsTo) return false;
    }
    return true;
};

export default CustomFilterPanel;
